import React, { useState, useRef, useEffect } from 'react';

/**
 * ResizablePanels - A component that creates resizable panels with draggable dividers
 * @param {Array} children - Array of React elements to render as panels
 * @param {Array} defaultSizes - Array of initial sizes (in percentages, should sum to 100)
 * @param {Function} onReset - Optional callback for reset button
 */
const ResizablePanels = ({ children, defaultSizes }) => {
  const [sizes, setSizes] = useState(defaultSizes);
  const [isDragging, setIsDragging] = useState(null);
  const containerRef = useRef(null);

  const handleMouseDown = (index) => {
    setIsDragging(index);
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging === null || !containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerWidth = containerRect.width;

      // Calculate mouse position relative to container
      const mouseX = e.clientX - containerRect.left;
      const percentage = (mouseX / containerWidth) * 100;

      // Update sizes
      const newSizes = [...sizes];
      const leftPanelIndex = isDragging;
      const rightPanelIndex = isDragging + 1;

      // Calculate the total size of left and right panels
      const totalSize = sizes[leftPanelIndex] + sizes[rightPanelIndex];

      // Ensure minimum size of 15% for each panel
      const minSize = 15;
      const maxLeftSize = totalSize - minSize;

      // Calculate cumulative size up to this divider
      let cumulativeSize = 0;
      for (let i = 0; i < leftPanelIndex; i++) {
        cumulativeSize += sizes[i];
      }

      // New left panel size
      let newLeftSize = percentage - cumulativeSize;

      // Clamp to min/max
      newLeftSize = Math.max(minSize, Math.min(maxLeftSize, newLeftSize));

      // Right panel takes the remaining space
      const newRightSize = totalSize - newLeftSize;

      newSizes[leftPanelIndex] = newLeftSize;
      newSizes[rightPanelIndex] = newRightSize;

      setSizes(newSizes);
    };

    const handleMouseUp = () => {
      setIsDragging(null);
    };

    if (isDragging !== null) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, sizes]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          width: '100%',
          height: '100%',
          position: 'relative'
        }}
      >
        {children.map((child, index) => (
          <React.Fragment key={index}>
            <div
              style={{
                width: `${sizes[index]}%`,
                height: '100%',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              {child}
            </div>

            {/* Divider */}
            {index < children.length - 1 && (
              <div
                onMouseDown={() => handleMouseDown(index)}
                style={{
                  width: '4px',
                  height: '100%',
                  background: isDragging === index ? 'var(--indigo-500)' : 'var(--border-light)',
                  cursor: 'ew-resize',
                  position: 'relative',
                  transition: isDragging === index ? 'none' : 'background 0.2s',
                  zIndex: 10
                }}
                onMouseEnter={(e) => {
                  if (isDragging === null) {
                    e.target.style.background = 'var(--indigo-500)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isDragging === null) {
                    e.target.style.background = 'var(--border-light)';
                  }
                }}
              >
                {/* Visual indicator on hover */}
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '20px',
                  height: '40px',
                  background: isDragging === index ? 'rgba(99, 102, 241, 0.3)' : 'transparent',
                  borderRadius: '4px',
                  pointerEvents: 'none',
                  transition: 'background 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--indigo-500)',
                  fontSize: '12px',
                  fontWeight: '700'
                }}>
                  ⋮
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Reset button - removed to avoid overlap with parent component buttons */}
    </div>
  );
};

export default ResizablePanels;
